import { en as vuetify } from 'vuetify/locale';
import type { Messages } from './de.ts';

/**
 * English. Typed against the German messages: a missing key and an extra key
 * are both compile errors, which makes `just typecheck` the parity gate.
 */
const en: Messages = {
  app: {
    name: 'StadtPuls',
    tagline: 'Operations overview',
    skipToContent: 'Skip to content',
  },
  nav: {
    menu: 'Menu',
    overview: 'Overview',
    devices: 'Devices',
    sites: 'Sites',
  },
  settings: {
    language: 'Language',
    theme: 'Appearance',
    themes: { light: 'Light', dark: 'Dark', system: 'Follow system' },
  },
  pages: {
    overview: 'Overview',
    devices: 'Devices',
    device: 'Device',
    twin: 'Site',
    notFound: 'Page not found',
  },
  notFound: {
    text: 'This address does not exist, or it points at something you may not see.',
    back: 'Back to the overview',
  },
  auth: {
    account: 'Account',
    logout: 'Sign out',
    unreachableTitle: 'Sign-in unavailable',
    unreachableText: 'The sign-in service is not responding. Please try again in a moment.',
    retry: 'Try again',
  },
  errors: {
    network: 'No connection to the server.',
    unauthenticated: 'Your session has expired.',
    forbidden: 'You do not have permission for this.',
    'not-found': 'Not found, or not in your area.',
    'invalid-request': 'The request was invalid.',
    internal: 'The server reported an error.',
    retry: 'Reload',
  },
  overview: {
    subtitle: 'Every site and device in your area',
    devices: 'Devices',
    devicesHint: 'at one site | at {n} sites',
    reporting: 'Reporting',
    reportingHint: 'in the last {hours} hours',
    silent: 'Silent',
    silentHint: 'no report for more than {hours} hours',
    never: 'Never reported',
    neverHint: 'since they were installed',
    asOf: 'As of {time}',
  },
  tree: {
    empty: 'There are no sites in your area.',
  },
  status: {
    reporting: 'Reporting',
    silent: 'Silent',
    never: 'Never reported',
  },
  devices: {
    search: 'Search name or identifier',
    status: 'Status',
    name: 'Name',
    twin: 'Site',
    externalId: 'Identifier',
    lastSeen: 'Last report',
    tags: 'Tags',
    never: 'never',
    unplaced: 'not assigned to a site',
    next: 'Next page',
    first: 'First page',
    empty: 'No devices found.',
    filteredByTwin: 'Only {title} and below',
    clearFilter: 'Remove filter',
    pageNote: 'Paged, sorted by {column}, without a total count.',
    sortAsc: 'sorted ascending',
    sortDesc: 'sorted descending',
  },
  twin: {
    subtitle: 'Site without devices | Site with one device | Site with {n} devices',
    children: 'Sub-sites',
    devices: 'Devices at this site and below',
    allDevices: 'All in the device list',
    masterData: 'Master data',
    noMasterData: 'No master data recorded.',
    coordinates: 'Coordinates',
    noCoordinates: 'No coordinates, so not on the map.',
    devicesShort: 'no devices | one device | {n} devices',
  },
  device: {
    identity: 'Device',
    externalId: 'Identifier',
    devEui: 'DevEUI',
    driver: 'Driver',
    noDriver: 'no driver',
    twin: 'Site',
    tags: 'Tags',
    lastSeen: 'Last report',
    latest: 'Current values',
    noValue: 'no value yet',
    noVariables: "This device's driver declares no variables.",
    yes: 'yes',
    no: 'no',
  },
  variables: {
    temperature: 'Temperature',
    humidity: 'Humidity',
    light: 'Light',
    motion: 'Motion',
    co2: 'CO₂',
    vdd: 'Supply voltage',
    volume: 'Meter reading',
    leak: 'Leak',
    battery_low: 'Battery low',
    backflow: 'Backflow',
    leak_count: 'Leaks total',
    battery_v: 'Battery voltage',
  },
  chart: {
    title: 'History',
    range: 'Range',
    variable: 'Measurement',
    ranges: { '24h': '24 h', '7d': '7 days', '30d': '30 days' },
    level: 'Level',
    consumption: 'Consumption per interval',
    empty: 'No readings in this range.',
    min: 'Min',
    max: 'Max',
    mean: 'Mean',
    at: 'at {time}',
    windowMean: 'Means per {every}',
    windowConsumption: 'Consumption per {every}',
    windowFlag: 'Per {every}: whether it triggered',
    zone: 'Times in {zone}',
    device: 'Device',
    describe: '{what}, {span}: minimum {min}, maximum {max} at {at}, mean {mean}.',
  },
  map: {
    title: 'Map',
    label: 'Map of the sites with devices',
    none: 'No site with devices has coordinates.',
    unplaced:
      'One site with devices has no coordinates and is therefore missing from the map: | {n} sites with devices have no coordinates and are therefore missing from the map:',
  },
  $vuetify: vuetify,
};

export default en;
